/**
 * modes/inline.ts — inline embed mode.
 *
 * Replaces the trigger element (or inserts after it) with a 640×720 iframe
 * wrapper directly in the page flow. Equivalent to [mp_widget] shortcode but
 * driven by data attributes and usable anywhere.
 *
 * The trigger element is hidden (not removed) so it can be re-shown if needed.
 */

import { EmbedConfig } from '../config'
import { createIframe } from '../iframe-factory'
import { el } from '../dom'
import { injectStyles } from '../styles'
import { listenForMessages } from '../postmessage'

export function mountInline(trigger: HTMLElement, config: EmbedConfig): () => void {
  injectStyles()

  const iframe = createIframe(config)
  const wrap   = el('div', { class: 'mp-inline-wrap' }, iframe)

  // Insert the wrapper immediately after the trigger element
  trigger.style.display = 'none'
  trigger.insertAdjacentElement('afterend', wrap)

  const unsubscribe = listenForMessages({
    onReady:           () => { /* no-op for inline */ },
    onStepChange:      () => { /* no-op for inline */ },
    onBookingComplete: () => {
      // For inline mode, nothing to close — booking is done in-place.
      // Host page JS can listen via window.ManagePatients.on('booking-complete')
    },
    onBookingError:    () => { /* no-op for inline */ },
    onCloseRequest:    () => {
      // Inline mode doesn't have a close affordance from the iframe.
      // Ignore mp:close-request in inline mode.
    },
    onHeightHint:      (height) => {
      // Auto-size to the booking content so there's no inner scrollbar or
      // dead space. Clamped so a bad value can't collapse or explode the page.
      // Only messages from this iframe resize it (several widgets per page).
      const h = Math.round(Math.min(4000, Math.max(360, height)))
      iframe.style.height = `${h}px`
    },
    source: iframe,
  })

  // Return cleanup function
  return () => {
    unsubscribe()
    wrap.remove()
    trigger.style.display = ''
  }
}
